import { DateTime, type DateTimeFormatOptions } from 'luxon';
import numeral from 'numeral';

export function parseDateTime(dateStr: string): DateTime {
  if (dateStr.includes('T')) {
    return DateTime.fromISO(dateStr);
  }
  return DateTime.fromSQL(dateStr);
}

// Callers pass through dates that may not be set yet, and luxon renders those as "Invalid DateTime"
// rather than failing — so an empty input is answered with an empty string before parsing.
function formatDateTime(dateStr: string, render: (parsed: DateTime) => string): string {
  if (!dateStr) {
    return '';
  }

  return render(parseDateTime(dateStr));
}

export function formatUpdatedDate(dateStr: string): string {
  return formatDateTime(dateStr, (parsed) => parsed.toFormat('D'));
}

export function formatUpdatedDateTime(dateStr: string): string {
  return formatDateTime(dateStr, (parsed) => parsed.toFormat('MMM d, hh:mm a'));
}

export function formatUpdatedTimestamp(dateStr: string): string {
  return formatDateTime(dateStr, (parsed) => parsed.toFormat('D t ZZZZ'));
}

export function formatDateMed(dateStr: string): string {
  return formatDateTime(dateStr, (parsed) => parsed.toLocaleString(DateTime.DATE_MED));
}

export function formatDate(dateStr: string, format: string): string {
  return formatDateTime(dateStr, (parsed) => parsed.toFormat(format));
}

export function formatDateLocale(dateStr: string, formatOpts?: DateTimeFormatOptions): string {
  return formatDateTime(dateStr, (parsed) => parsed.toLocaleString(formatOpts));
}

export function formatDateMedNonLocale(dateStr: string): string {
  return formatDate(dateStr, 'MMM dd, yyyy');
}

export function formatTime(dateStr: string): string {
  return formatDate(dateStr, 'HH:mm');
}

/**
 * calculates difference between given time and now
 * @param dateStr date from the past
 * @returns {{unit: string, count: number}} returns the biggest non-zero unit of the time diff (year max, single or plural) and it's counter (floor round)
 */
export const getDateTimeDiffNow = (dateStr: string): { unit: string; count: number } => {
  const parsed = parseDateTime(dateStr);

  // diffNow returns a negative value for date from the past, hence negate()
  const diffNow = parsed.diffNow(['year', 'month', 'day', 'hour', 'minute', 'second']).negate();

  let unit, count;

  if (diffNow.years) {
    unit = 'years';
    count = Math.floor(diffNow.years);
  } else if (diffNow.months) {
    unit = 'months';
    count = Math.floor(diffNow.months);
  } else if (diffNow.weeks) {
    unit = 'weeks';
    count = Math.floor(diffNow.weeks);
  } else if (diffNow.days) {
    unit = 'days';
    count = Math.floor(diffNow.days);
  } else if (diffNow.hours) {
    unit = 'hours';
    count = Math.floor(diffNow.hours);
  } else if (diffNow.minutes) {
    unit = 'minutes';
    count = Math.floor(diffNow.minutes);
  } else {
    unit = 'seconds';
    count = Math.floor(diffNow.seconds);
  }

  if (count === 1) {
    unit = unit.slice(0, -1); //remove plural s from the unit
  }

  return { unit, count };
};

export const formatDateTimeDiffNow = (dateStr: string): string => {
  const { unit, count } = getDateTimeDiffNow(dateStr);
  return `${count} ${unit} ago`;
};

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
];
export const numberToWord = (num: number): string => {
  const rounded = Math.round(num);
  if (rounded < 0 || rounded >= 12) {
    return `${num}`;
  }
  return NUMBER_WORDS[rounded];
};

export const nthNumber = (number: number): string => {
  if (number > 3 && number < 21) return `${number}th`;
  switch (number % 10) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
};

export const formatMoney = (num: number): string => {
  return numeral(num).format('$0,0[.]00');
};

export const formatMoneyFixedTrailing = (num: number): string => {
  return numeral(num).format('$0,0.00');
};

export const formatNumber = (num: number): string => {
  return Number(num).toLocaleString('en');
};

export const formatPercentage = (num: number, format: string): string => {
  return `${numeral(num).format(format || '0[.]0[0]')}%`;
};

export const formatPercentageWithOneDecimal = (num: number): string => {
  return `${numeral(num).format('0.0')}%`;
};

export const pluralize = (count: number, single: string, plural: string): string => {
  if (count === 1) {
    return single;
  }
  return plural != null ? plural : `${single}s`;
};

export const formatByObject = (value: string, obj: Record<string, string>) => {
  if (!value || !obj) return value;

  let str = value.toString();

  Object.keys(obj).forEach((key) => {
    str = str.replace(new RegExp(`\\{${key}\\}`, 'gi'), obj[key]);
  });

  return str;
};

export const formatFileSize = (file: File, decimalPlaces: number = 2): string => {
  if (file?.size >= 1048576) {
    return `${(file?.size / 1048576).toFixed(decimalPlaces)}MB`;
  } else {
    return `${(file?.size / 1024).toFixed(decimalPlaces)}KB`;
  }
};
